import React from 'react';

import {
  ApiInterfaces,
  AuthTypes,
  DeviceTypes,
  HttpError,
} from '@flashbacktech/flashbackclient/api';

import { useApi } from './ApiContext';
import { toastError } from '../utils/toast';
import { initializeGoogleOAuth } from '../services/googleAuth';
import { initializeMicrosoftOAuth } from '../services/microsoftAuth';
import { DeviceFingerprinter } from '../services/deviceManager';
import { WorkspaceTypes } from '@flashbacktech/flashbackclient/api';
import { processAuthError, createWithTokenRefresh } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { debugAuthLog, debugAuthError } from '../utils/debugAuth';

// Global app data interfaces
interface UserProfile {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  orgId?: string;
  isBusiness?: boolean;
  mfaRequired?: boolean;
  orgRoles?: number[]; // Array of org role flags (0x00=USER, 0x01=BILLING, 0x02=WORKSPACES, 0xfe=ADMIN, 0xff=OWNER)
}

interface Organization {
  id: string;
  domain: string;
  name?: string;
  reposDisabled?: boolean;
}

export interface Workspace {
  id: string;
  name: string;
  userRole?: 'ADMIN' | 'READ' | 'WRITE'; // Current user's role in this workspace
}

interface GlobalAppData {
  userProfile: UserProfile | null;
  organization: Organization | null;
  workspaces: Workspace[];
  selectedWorkspaceId: string;
  hasOrgAccess: boolean;
  isLoading: boolean;
  isReady: boolean; // All critical data loaded
}

interface AuthContextType {
  authState: AuthTypes.AuthState;
  setAuthState: (authState: AuthTypes.AuthState) => void;
  handleGoogleSignIn: () => Promise<void>;
  handleGoogleCallback: (code: string) => Promise<string | null>; // Returns redirect path or null
  handleMicrosoftSignIn: () => Promise<void>;
  handleMicrosoftCallback: (code: string) => Promise<string | null>;
  handleGithubSignIn: () => void;
  handleGithubCallback: (code: string) => Promise<void>;
  handleWeb3Login: (connectionResult: AuthTypes.Web3RegisterBody) => Promise<void>;
  handleSignOut: (redirectTo?: string) => void;
  isAuthenticated: boolean;
  globalData: GlobalAppData;
  setSelectedWorkspace: (workspaceId: string) => void;
  refreshGlobalData: (forceRefresh?: boolean) => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { apiClient } = useApi();
  const navigate = useNavigate();
  const [authState, setAuthState] = React.useState<AuthTypes.AuthState>(() => {
    const saved = localStorage.getItem('authState');

    if (saved) {
      try {
        const parsed = JSON.parse(saved);

        // Check if last login was too long ago (30 days)
        // If yes, force a complete refresh by clearing localStorage
        const lastLoginDate = localStorage.getItem('lastLoginDate');
        if (lastLoginDate) {
          const daysSinceLogin = (Date.now() - parseInt(lastLoginDate, 10)) / (1000 * 60 * 60 * 24);
          if (daysSinceLogin > 30) {
            debugAuthLog('[Auth] Last login was', daysSinceLogin.toFixed(0), 'days ago. Forcing refresh...');
            localStorage.clear();
            window.location.reload();
            return {
              user: null,
              token: null,
              accessToken: null,
              refreshToken: null,
              expiresAt: null,
            };
          }
        }

        // Ensure the token is set in the API client immediately
        if (parsed.token) {
          apiClient.setAuthToken(parsed.token);
        }

        return parsed;
      } catch (error) {
        localStorage.removeItem('authState');
      }
    } else {
      /* No saved state */
    }

    return {
      user: null,
      token: null,
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
    };
  });

  // Global app data state
  const [globalData, setGlobalData] = React.useState<GlobalAppData>(() => ({
    userProfile: null,
    organization: null,
    workspaces: [],
    selectedWorkspaceId: (() => {
      try {
        const saved = localStorage.getItem('selectedWorkspaceId');
        // Don't use 'ALL' anymore
        if (saved && saved !== 'ALL') {
          return saved;
        }
        return ''; // Will be set to first workspace when loaded
      } catch (e) {
        return '';
      }
    })(),
    hasOrgAccess: false,
    isLoading: false,
    isReady: false,
  }));

  // Helper to sleep for retries
  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  // Cache constants
  const GLOBAL_DATA_CACHE_KEY = 'global_data_cache';
  const GLOBAL_DATA_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

  // Create withTokenRefresh function that doesn't use hooks (to avoid circular dependency)
  const withTokenRefresh = React.useMemo(
    () => createWithTokenRefresh(apiClient, authState, setAuthState, navigate),
    [apiClient, authState, setAuthState, navigate]
  );

  // Centralized data loader with retries
  const loadGlobalData = React.useCallback(async (forceRefresh = false) => {
    if (!authState || !authState.token) {
      setGlobalData((prev) => ({ ...prev, isLoading: false, isReady: false }));
      return;
    }

    // Skip loading when MFA verification is pending — the token is temporary
    // and full-auth endpoints will reject it with 403. loadGlobalData will be
    // triggered again after MFA verification succeeds and the token is upgraded.
    try {
      if (sessionStorage.getItem('mfa_pending') === 'true') {
        debugAuthLog('[Auth] loadGlobalData skipped: MFA verification pending');
        return;
      }
    } catch { /* ignore sessionStorage errors */ }

    // Try to load from cache first (unless force refresh)
    if (!forceRefresh) {
      try {
        const cached = localStorage.getItem(GLOBAL_DATA_CACHE_KEY);
        if (cached) {
          const cacheData = JSON.parse(cached);
          const cacheAge = Date.now() - cacheData.timestamp;

          // Use cache if less than 10 minutes old
          if (cacheAge < GLOBAL_DATA_CACHE_DURATION) {
            debugAuthLog('[Auth] Using cached global data (age:', Math.round(cacheAge / 1000), 's)');
            setGlobalData((prev) => ({
              ...prev,
              userProfile: cacheData.userProfile,
              organization: cacheData.organization,
              workspaces: cacheData.workspaces,
              selectedWorkspaceId: prev.selectedWorkspaceId || cacheData.selectedWorkspaceId,
              hasOrgAccess: cacheData.hasOrgAccess,
              isLoading: false,
              isReady: true,
            }));
            return;
          }
        }
      } catch (error) {
        debugAuthError('[Auth] Cache read error:', error);
      }
    }

    setGlobalData((prev) => ({ ...prev, isLoading: true, isReady: false }));

    try {
      let userProfile: UserProfile | null = null;
      let organization: Organization | null = null;
      let workspaces: Workspace[] = [];
      let hasOrgAccess = false;
 
      const profileResp = await withTokenRefresh(() => apiClient.getUserProfile());
      if (profileResp?.success && profileResp?.data) {
        const profileData = profileResp.data;
        
        userProfile = {
          id: profileData.id || '',
          email: profileData.email || '',
          name: profileData.name || '',
          firstName: profileData.name || undefined,
          lastName: profileData.lastName || undefined,
          orgId: profileData.orgId || '',
          isBusiness: false, // Will be updated from organization record
          orgRoles: (profileData as any).orgRoles || [], // Array of org role flags
          mfaRequired: typeof (profileData as any).mfaRequired === 'boolean' ? (profileData as any).mfaRequired : undefined,
        };

        // Get organization and check is_business from organization record
        const orgId = userProfile?.orgId;
        if (orgId && orgId !== '') {
          const orgResp = await withTokenRefresh(() => apiClient.getOrganization(orgId));
          if (orgResp?.success && orgResp?.data) {
            // Cast to access is_business which may not be in the type definition
            const orgData = orgResp.data as typeof orgResp.data & { is_business?: boolean };
            
            debugAuthLog('[Auth] Organization API response:', JSON.stringify(orgResp.data, null, 2));
            
            organization = {
              id: orgId,
              domain: orgData.domain || '',
              name: orgData.name || '',
              reposDisabled: orgData.reposDisabled === true,
            };
            
            // hasOrgAccess is determined by is_business flag from organization record
            hasOrgAccess = orgData.is_business === true;
            userProfile.isBusiness = orgData.is_business === true;
            debugAuthLog('[Auth] hasOrgAccess from org.is_business:', hasOrgAccess);
          }
        }
      }
      
      const wsResp = await apiClient.getWorkspaces();
      if (wsResp?.success && wsResp?.workspaces) {
        const currentUserEmail = userProfile?.email?.toLowerCase();
        workspaces = (wsResp.workspaces || []).map((w: any) => {
          // Find current user's role in this workspace
          let userRole: 'ADMIN' | 'READ' | 'WRITE' | undefined;
          if (currentUserEmail && w.users) {
            const userEntry = w.users.find((u: any) => 
              u.user?.email?.toLowerCase() === currentUserEmail
            );
            if (userEntry?.role) {
              userRole = userEntry.role as 'ADMIN' | 'READ' | 'WRITE';
            }
          }
          return { id: w.id, name: w.name, userRole };
        });
      }

      // Update global data
      setGlobalData((prev) => {
        let selectedId = prev.selectedWorkspaceId;
        
        // If no workspace selected, 'ALL' selected, or selected workspace doesn't exist anymore,
        // default to first workspace
        if (!selectedId || selectedId === 'ALL' || !workspaces.find(w => w.id === selectedId)) {
          if (workspaces.length > 0) {
            selectedId = workspaces[0].id;
            // Persist the first workspace as default
            try {
              localStorage.setItem('selectedWorkspaceId', selectedId);
              localStorage.setItem('lastSelectedWorkspaceIdNonAll', selectedId);
            } catch (e) {
              void 0;
            }
          }
        }
        
        const updated = {
          ...prev,
          userProfile,
          organization,
          workspaces,
          selectedWorkspaceId: selectedId,
          hasOrgAccess,
          isLoading: false,
          isReady: true,
        };

        // Save to cache
        try {
          localStorage.setItem(GLOBAL_DATA_CACHE_KEY, JSON.stringify({
            userProfile,
            organization,
            workspaces,
            selectedWorkspaceId: selectedId,
            hasOrgAccess,
            timestamp: Date.now(),
          }));
          debugAuthLog('[Auth] Global data cached');
        } catch (e) {
          debugAuthError('[Auth] Failed to cache global data:', e);
        }

        // Dispatch events for backward compatibility
        setTimeout(() => {
          try {
            window.dispatchEvent(new Event('workspacesUpdated'));
            const evt = new CustomEvent('workspaceChanged', {
              detail: { workspaceId: updated.selectedWorkspaceId },
            });
            window.dispatchEvent(evt);
          } catch (e) {
            void 0;
          }
        }, 0);

        return updated;
      });
    } catch (error) {
      setGlobalData((prev) => ({ ...prev, isLoading: false, isReady: false }));
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  // Load global data when user logs in
  const hasTriedRetryRef = React.useRef(false);
  
  React.useEffect(() => {
    if (authState && authState.token) {
      hasTriedRetryRef.current = false; // Reset retry flag on new login
      loadGlobalData();
    }
  }, [authState?.token, loadGlobalData]);
  
  // Auto-retry mechanism if organization data didn't load properly
  React.useEffect(() => {
    if (!authState?.token || hasTriedRetryRef.current) return;
    
    const retryTimer = setTimeout(() => {
      const currentProfile = globalData.userProfile;
      const currentOrg = globalData.organization;
      
      // If user has orgId but organization is null, retry once
      if (currentProfile?.orgId && !currentOrg) {
        debugAuthLog('[Auth] Organization data missing after initial load. Retrying now...');
        hasTriedRetryRef.current = true;
        loadGlobalData();
      }
    }, 1500); // Wait 1.5 seconds before checking
    
    return () => clearTimeout(retryTimer);
  }, [authState?.token, globalData.userProfile, globalData.organization, loadGlobalData]);
  
  // Listen for forced workspace reload (e.g., after creating a new workspace)
  React.useEffect(() => {
    if (!authState?.token) return;
    
    const handleForceReload = async () => {
      debugAuthLog('[Auth] Force reload workspaces event received');
      try {
        const wsResponse = await apiClient.getWorkspaces();
        
        if (wsResponse.success && wsResponse.workspaces) {
          setGlobalData((prev) => {
            const currentUserEmail = prev.userProfile?.email?.toLowerCase();
            return {
              ...prev,
              workspaces: (wsResponse.workspaces || []).map((w: any) => {
                // Find current user's role in this workspace
                let userRole: 'ADMIN' | 'READ' | 'WRITE' | undefined;
                if (currentUserEmail && w.users) {
                  const userEntry = w.users.find((u: any) => 
                    u.user?.email?.toLowerCase() === currentUserEmail
                  );
                  if (userEntry?.role) {
                    userRole = userEntry.role as 'ADMIN' | 'READ' | 'WRITE';
                  }
                }
                return { id: w.id, name: w.name, userRole };
              }),
            };
          });
          
          // Dispatch workspacesUpdated event for other components
          try {
            window.dispatchEvent(new Event('workspacesUpdated'));
          } catch (e) {
            void 0;
          }
        }
      } catch (e) {
        debugAuthError('[Auth] Failed to reload workspaces:', e);
      }
    };
    
    window.addEventListener('forceReloadWorkspaces', handleForceReload);
    return () => window.removeEventListener('forceReloadWorkspaces', handleForceReload);
  }, [authState?.token]);

  // Workspace selection handler
  const setSelectedWorkspace = React.useCallback((workspaceId: string) => {
    // Update globalData state (source of truth)
    setGlobalData((prev) => {
      // Update the cache with new selected workspace
      try {
        const cached = localStorage.getItem(GLOBAL_DATA_CACHE_KEY);
        if (cached) {
          const cacheData = JSON.parse(cached);
          cacheData.selectedWorkspaceId = workspaceId;
          cacheData.timestamp = Date.now(); // Refresh timestamp
          localStorage.setItem(GLOBAL_DATA_CACHE_KEY, JSON.stringify(cacheData));
        }
      } catch (e) {
        void 0;
      }
      return { ...prev, selectedWorkspaceId: workspaceId };
    });
    
    // Persist to localStorage for cross-tab sync and fallback
    try {
      localStorage.setItem('lastSelectedWorkspaceIdNonAll', workspaceId);
      localStorage.setItem('selectedWorkspaceId', workspaceId);
    } catch {
      // Ignore localStorage errors
    }
    
    // Dispatch events to notify other components (for backward compatibility)
    try {
      window.dispatchEvent(new CustomEvent('workspaceChanged', { detail: { workspaceId } }));
      window.dispatchEvent(new Event('bucketsUpdated'));
    } catch {
      // Ignore event dispatch errors
    }
    try {
      localStorage.setItem('selectedWorkspaceId', workspaceId);
    } catch (e) {
      void 0;
    }
    try {
      const evt = new CustomEvent('workspaceChanged', { detail: { workspaceId } });
      window.dispatchEvent(evt);
    } catch (e) {
      void 0;
    }
  }, []);

  // Update localStorage and set auth token when authState changes
  React.useEffect(() => {
    if (!authState) return;

    if (authState.user || authState.token) {
      localStorage.setItem('authState', JSON.stringify(authState));
      apiClient.setAuthToken(authState.token);
      
      // Save last login date for session freshness check
      // Only update if this is a new login (not just a page reload with existing token)
      const existingLoginDate = localStorage.getItem('lastLoginDate');
      if (!existingLoginDate || authState.token) {
        localStorage.setItem('lastLoginDate', Date.now().toString());
      }
    } else {
      localStorage.removeItem('authState');
      apiClient.setAuthToken(null);
    }
  }, [authState, apiClient]);

  const handleGoogleSignIn = async (): Promise<void> => {
    try {
      const authUrl = initializeGoogleOAuth();
      window.location.href = authUrl;
    } catch (error: unknown) {
      if (error instanceof HttpError) {
        /* HTTP error logged */
      } else {
        /* Unknown error */
      }
      toastError('Failed to sign in with Google');
    }
  };

  const handleGoogleCallback = async (code: string): Promise<string | null> => {
    try {
      // Check if we have an activation token in local storage
      const activationUid = localStorage.getItem('activation_uid');
      const activationToken = localStorage.getItem('activation_token');

      const authResult = await apiClient.exchangeCode(
        code, 
        ApiInterfaces.ProviderType.GOOGLE,
        activationUid || undefined,
        activationToken || undefined
      );
      // Get device information using the utility
      const deviceFingerprinter = DeviceFingerprinter.getInstance();
      const deviceInfo: DeviceTypes.DeviceInfo = await deviceFingerprinter.getDeviceInfoForAuth();

      if (authResult.success) {
        // Determine authState from response
        let finalAuthState: AuthTypes.AuthState | null = null;
        const authResultAny = authResult as any;

        if (authResult.authState) {
          // If authState is provided directly
          finalAuthState = authResult.authState;
        } else if (authResultAny.fullAccessToken || authResultAny.user) {
          // Construct authState from response fields (fallback)
          finalAuthState = {
            user: authResultAny.user || null,
            token: authResultAny.fullAccessToken || authResultAny.token || null,
            accessToken: authResultAny.fullAccessToken || authResultAny.accessToken || null,
            refreshToken: authResultAny.refreshToken || null,
            expiresAt: authResultAny.expiresAt || null,
          };
        }

        const signupEmail = localStorage.getItem('signupEmail');
        console.log('@@@ finalAuthState', finalAuthState?.user?.email, ' -> signupEmail', signupEmail);

        if (signupEmail && finalAuthState?.user?.email !== signupEmail) {
          toastError('The email you are trying to use is not the one you received the activation link for. Please request a new activation link.');
          return null;
        }

        if (finalAuthState) {
          await apiClient.authenticate(
            finalAuthState.token || '',
            ApiInterfaces.ProviderType.GOOGLE,
            deviceInfo
          );
          setAuthState(finalAuthState);

          try {
            localStorage.setItem('loginProvider', 'google');
            localStorage.removeItem('activation_uid');
            localStorage.removeItem('activation_token');
            localStorage.removeItem('signupEmail');
          } catch (_err) {
            /* noop: ignore storage errors (private mode, quota, etc.) */
          }
          // After successful authentication, check MFA requirements
          // The MFA context will handle the post-login flow
          return null; // No special redirect needed
        } else {
          toastError('Failed to sign in with Google');
          return null;
        }
      } else {
        toastError('Failed to sign in with Google');
        return null;
      }
    } catch (error: unknown) {
      if (error instanceof HttpError) {
        const errorCode = error.data?.error_code;
        const serverMessage = error.data?.message;

        if (errorCode === 'ACTIVATION_REQUIRED') {
          toastError(serverMessage || 'No account found. Please sign up to create your Flashgate account.');
          navigate('/auth?tab=signup');
          return null;
        }
        if (errorCode === 'EMAIL_EXISTS_INACTIVE') {
          toastError('This account has been deactivated. Please contact support.');
          return null;
        }
        if (error.status === 404) {
          toastError('The activation link is invalid or has expired. Please request a new activation link.', 10000);
          return null;
        }
        if (error.status === 401) {
          const errorMessage = processAuthError(errorCode);
          toastError(errorMessage);
          return null;
        }
        toastError(serverMessage || 'Failed to sign in with Google');
        return null;
      }
      toastError('Failed to sign in with Google');
      return null;
    }
  };

  const handleMicrosoftSignIn = async (): Promise<void> => {
    try {
      const authUrl = initializeMicrosoftOAuth();
      window.location.href = authUrl;
    } catch (error: unknown) {
      toastError('Failed to sign in with Microsoft');
    }
  };

  const handleMicrosoftCallback = async (code: string): Promise<string | null> => {
    try {
      const activationUid = localStorage.getItem('activation_uid');
      const activationToken = localStorage.getItem('activation_token');

      const authResult = await apiClient.exchangeCode(
        code,
        'MICROSOFT' as ApiInterfaces.ProviderType,
        activationUid || undefined,
        activationToken || undefined
      );

      const deviceFingerprinter = DeviceFingerprinter.getInstance();
      const deviceInfo: DeviceTypes.DeviceInfo = await deviceFingerprinter.getDeviceInfoForAuth();

      if (authResult.success) {
        let finalAuthState: AuthTypes.AuthState | null = null;
        const authResultAny = authResult as any;

        if (authResult.authState) {
          finalAuthState = authResult.authState;
        } else if (authResultAny.fullAccessToken || authResultAny.user) {
          finalAuthState = {
            user: authResultAny.user || null,
            token: authResultAny.fullAccessToken || authResultAny.token || null,
            accessToken: authResultAny.fullAccessToken || authResultAny.accessToken || null,
            refreshToken: authResultAny.refreshToken || null,
            expiresAt: authResultAny.expiresAt || null,
          };
        }

        const signupEmail = localStorage.getItem('signupEmail');
        if (signupEmail && finalAuthState?.user?.email !== signupEmail) {
          toastError('The email you are trying to use is not the one you received the activation link for. Please request a new activation link.');
          return null;
        }

        if (finalAuthState) {
          await apiClient.authenticate(
            finalAuthState.token || '',
            'MICROSOFT' as ApiInterfaces.ProviderType,
            deviceInfo
          );
          setAuthState(finalAuthState);

          try {
            localStorage.setItem('loginProvider', 'microsoft');
            localStorage.removeItem('activation_uid');
            localStorage.removeItem('activation_token');
            localStorage.removeItem('signupEmail');
          } catch (_err) {
            /* noop */
          }
          return null;
        } else {
          toastError('Failed to sign in with Microsoft');
          return null;
        }
      } else {
        toastError('Failed to sign in with Microsoft');
        return null;
      }
    } catch (error: unknown) {
      if (error instanceof HttpError) {
        const errorCode = error.data?.error_code;
        const serverMessage = error.data?.message;

        if (errorCode === 'ACTIVATION_REQUIRED') {
          toastError(serverMessage || 'No account found. Please sign up to create your Flashgate account.');
          navigate('/auth?tab=signup');
          return null;
        }
        if (errorCode === 'EMAIL_EXISTS_INACTIVE') {
          toastError('This account has been deactivated. Please contact support.');
          return null;
        }
        if (error.status === 404) {
          toastError('The activation link is invalid or has expired. Please request a new activation link.', 10000);
          return null;
        }
        if (error.status === 401) {
          const errorMessage = processAuthError(errorCode);
          toastError(errorMessage);
          return null;
        }
        toastError(serverMessage || 'Failed to sign in with Microsoft');
        return null;
      }
      toastError('Failed to sign in with Microsoft');
      return null;
    }
  };

  const handleGithubSignIn = () => {
    const githubClientId = process.env.REACT_APP_GITHUB_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/github/callback');
    const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${githubClientId}&redirect_uri=${redirectUri}&scope=user:email`;

    window.location.href = githubAuthUrl;
  };

  const handleGithubCallback = async (code: string) => {
    try {
      const activationUid = localStorage.getItem('activation_uid');
      const activationToken = localStorage.getItem('activation_token');

      const deviceFingerprinter = DeviceFingerprinter.getInstance();
      const deviceInfo: DeviceTypes.DeviceInfo = await deviceFingerprinter.getDeviceInfoForAuth();
      const authResult = await apiClient.authenticate(
        code,
        ApiInterfaces.ProviderType.GITHUB,
        deviceInfo,
        activationUid || undefined,
        activationToken || undefined
      );

      if (authResult.success) {
        try {
          localStorage.setItem('loginProvider', 'github');
          localStorage.removeItem('activation_uid');
          localStorage.removeItem('activation_token');
          localStorage.removeItem('signupEmail');
        } catch (_err) {
          /* noop: ignore storage errors (private mode, quota, etc.) */
        }

        // GitHub API returns different structure: fullAccessToken, user, etc.
        // Need to construct authState from the response
        const authResultAny = authResult as any;

        if (authResult.authState) {
          // If authState is provided directly
          setAuthState(authResult.authState);
        } else if (authResultAny.fullAccessToken || authResultAny.user) {
          // Construct authState from GitHub response fields
          const newAuthState: AuthTypes.AuthState = {
            user: authResultAny.user || null,
            token: authResultAny.fullAccessToken || authResultAny.token || null,
            accessToken: authResultAny.fullAccessToken || authResultAny.accessToken || null,
            refreshToken: authResultAny.refreshToken || null,
            expiresAt: authResultAny.expiresAt || null,
          };
          setAuthState(newAuthState);
        } else {
          toastError('Failed to sign in with GitHub.');
        }
      } else {
        toastError('Failed to sign in with GitHub');
      }
    } catch (error: unknown) {
      if (error instanceof HttpError) {
        const errorCode = (error as HttpError & { data?: { error_code?: string; message?: string } }).data?.error_code;
        const serverMessage = (error as HttpError & { data?: { message?: string } }).data?.message;

        if (errorCode === 'ACTIVATION_REQUIRED') {
          toastError(serverMessage || 'No account found. Please sign up to create your Flashgate account.');
          navigate('/auth?tab=signup');
        } else if (errorCode === 'EMAIL_EXISTS_INACTIVE') {
          toastError('This account has been deactivated. Please contact support.');
        } else if (errorCode === 'ORG_DISABLED' || errorCode === 'NO_ORGANIZATION') {
          toastError(serverMessage || 'Your organization account is not active. Please contact support.');
        } else {
          toastError(serverMessage || 'Failed to sign in with GitHub');
        }
      } else {
        toastError('Failed to sign in with GitHub');
      }
    }
  };

  const handleWeb3Login = async (connectionResult: AuthTypes.Web3RegisterBody) => {
    try {
      const response = await apiClient.web3Authenticate(connectionResult);

      if (response.accessToken && response.refreshToken && response.user) {
        setAuthState({
          user: {
            email: connectionResult.address,
            name: `Wallet ${connectionResult.address.slice(0, 8)}...`,
            imageUrl: '',
          },
          token: response.accessToken,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          expiresAt: response.expiresAt || null,
          provider: ApiInterfaces.ProviderType.WEB3_STELLAR,
        });
        // Navigate to dashboard after successful authentication
        window.location.href = '/';
      } else {
        toastError(response.message || 'Web3 authentication failed');
      }
    } catch (error) {
      console.error('Web3 authentication error:', error);
      toastError(error instanceof Error ? error.message : 'Web3 authentication failed');
    }
  };

  const handleSignOut = ( redirectTo = '/' ) => {
    try {
      // Clear ALL localStorage data on logout for a clean slate
      debugAuthLog('[Auth] Signing out and clearing all localStorage data');
      localStorage.clear();
    } catch (_err) {
      /* noop: ignore storage errors (private mode, quota, etc.) */
    }
    setAuthState({
      user: null,
      token: null,
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
    });
    window.location.href = redirectTo;
  };

  // Update last activity timestamp
  const updateLastActivity = React.useCallback(() => {
    try {
      localStorage.setItem('lastActivityTime', Date.now().toString());
    } catch (_err) {
      /* noop: ignore storage errors */
    }
  }, []);

  // Auto logout after 4 hours of inactivity
  React.useEffect(() => {
    if (!authState || !authState.token) return;

    // Set initial activity time on login
    updateLastActivity();

    // Check every minute if session has expired
    const SESSION_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
    const CHECK_INTERVAL = 60 * 1000; // Check every minute
    /*
    const interval = setInterval(() => {
      try {
        const lastActivity = localStorage.getItem('lastActivityTime');
        if (lastActivity) {
          const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
          if (timeSinceLastActivity > SESSION_TIMEOUT) {
            toastError('Your session has expired. Please log in again.');
            handleSignOut();
          }
        }
      } catch (error) {
        // Ignore errors
      }
    }, CHECK_INTERVAL);
    */
    // Update activity timestamp on user interactions
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleActivity = () => updateLastActivity();

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      //clearInterval(interval);
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [authState?.token, updateLastActivity]);

  const value = {
    authState,
    setAuthState,
    handleGoogleSignIn,
    handleGoogleCallback,
    handleMicrosoftSignIn,
    handleMicrosoftCallback,
    handleGithubSignIn,
    handleGithubCallback,
    handleWeb3Login,
    handleSignOut,
    isAuthenticated: !!(authState && authState.token),
    globalData,
    setSelectedWorkspace,
    refreshGlobalData: loadGlobalData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Centralized hook for getting the currently selected workspace ID.
 * 
 * Priority order:
 * 1. globalData.selectedWorkspaceId (source of truth from AuthContext)
 * 2. localStorage.getItem('selectedWorkspaceId') (fallback)
 * 3. 'ALL' (last resort, should rarely happen)
 * 
 * This hook automatically:
 * - Syncs with globalData changes
 * - Listens to workspaceChanged events
 * - Listens to localStorage changes (cross-tab sync)
 * 
 * @returns The currently selected workspace ID (never empty, defaults to 'ALL' if no workspace selected)
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const selectedWorkspaceId = useSelectedWorkspace();
 *   // Use selectedWorkspaceId in API calls, etc.
 * }
 * ```
 */
export const useSelectedWorkspace = (): string => {
  const { globalData } = useAuth();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState<string>(() => {
    // Initial value: prioritize globalData, then localStorage
    if (globalData?.isReady && globalData?.selectedWorkspaceId && 
        globalData.selectedWorkspaceId !== 'ALL' && globalData.selectedWorkspaceId !== '') {
      return globalData.selectedWorkspaceId;
    }
    try {
      const saved = localStorage.getItem('selectedWorkspaceId');
      if (saved && saved !== 'ALL' && saved !== '') {
        return saved;
      }
    } catch {
      // Ignore localStorage errors
    }
    return 'ALL';
  });

  // Sync with globalData when it changes
  React.useEffect(() => {
    if (globalData?.isReady && globalData?.selectedWorkspaceId) {
      const workspaceId = globalData.selectedWorkspaceId;
      if (workspaceId !== 'ALL' && workspaceId !== '') {
        setSelectedWorkspaceId(workspaceId);
        // Keep localStorage in sync
        try {
          localStorage.setItem('selectedWorkspaceId', workspaceId);
        } catch {
          // Ignore localStorage errors
        }
      }
    }
  }, [globalData?.isReady, globalData?.selectedWorkspaceId]);

  // Listen for workspace changes from Header/Sidebar
  React.useEffect(() => {
    const handleWorkspaceChanged = (e: Event) => {
      const customEvent = e as CustomEvent<{ workspaceId: string }>;
      const workspaceId = customEvent?.detail?.workspaceId;
      if (workspaceId) {
        if (workspaceId === 'ALL') {
          setSelectedWorkspaceId('ALL');
        } else {
          setSelectedWorkspaceId(workspaceId);
        }
      }
    };

    // Listen for cross-tab localStorage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'selectedWorkspaceId' && e.newValue) {
        const newValue = e.newValue;
        // Only update if it's a valid workspace ID (not 'ALL' or empty)
        if (newValue !== 'ALL' && newValue !== '') {
          setSelectedWorkspaceId(newValue);
        } else if (newValue === 'ALL') {
          setSelectedWorkspaceId('ALL');
        }
      }
    };

    window.addEventListener('workspaceChanged', handleWorkspaceChanged);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('workspaceChanged', handleWorkspaceChanged);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return selectedWorkspaceId;
};

/**
 * Centralized hook for getting the list of workspaces.
 * 
 * This hook provides access to the workspace list from AuthContext's globalData.
 * The list is loaded once at login and cached. Use `refreshGlobalData(true)` 
 * to force-reload after creating/deleting workspaces.
 * 
 * @returns Array of workspaces (id, name). Empty array if not loaded yet.
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const workspaces = useWorkspaces();
 *   const { refreshGlobalData } = useAuth();
 *   
 *   // After creating a workspace:
 *   await refreshGlobalData(true);
 * }
 * ```
 */
export const useWorkspaces = (): Workspace[] => {
  const { globalData } = useAuth();
  return globalData.workspaces || [];
};

/**
 * Combined hook for workspace data with reload trigger.
 * 
 * Use this when you need both the selected workspace ID and want to 
 * trigger data reloads when the workspace changes.
 * 
 * @returns Object with selectedWorkspaceId, workspaces, and reloadTick
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { selectedWorkspaceId, workspaces, reloadTick, isReady } = useWorkspaceData();
 *   
 *   useEffect(() => {
 *     // Reload data when workspace changes
 *     loadData();
 *   }, [selectedWorkspaceId, reloadTick]);
 * }
 * ```
 */
export const useWorkspaceData = () => {
  const { globalData } = useAuth();
  const selectedWorkspaceId = useSelectedWorkspace();
  const [reloadTick, setReloadTick] = React.useState(0);

  // Increment reloadTick when workspace changes
  React.useEffect(() => {
    setReloadTick((t) => t + 1);
  }, [selectedWorkspaceId]);

  return {
    selectedWorkspaceId,
    workspaces: globalData.workspaces || [],
    reloadTick,
    isReady: globalData.isReady,
  };
};
